import { useMemo, useState } from "react";
import { Topbar, AddPropertyButton } from "../components/Topbar";
import { KPICard } from "../components/KPICard";
import { StatusBadge } from "../components/StatusBadge";
import { AddPropertyModal } from "../components/AddPropertyModal";
import { useAuth } from "../context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Building2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  CircleX,
  ChevronRight,
} from "lucide-react";
import {
  PROPERTIES,
  OWNERS,
  getOwner,
  getKPIs,
  PROP_STATUS_TONE,
  STATUS_LABELS,
} from "../data/mockData";
import { useNavigate } from "react-router-dom";

const COUNTRIES = ["All", "Mallorca", "Ibiza"];
const CITIES = [
  "All",
  "Palma",
  "Deià",
  "Llucmajor",
  "Santa Eulària des Riu",
  "Sant Joan de Labritja",
  "Sant Josep de sa Talaia",
];
const STATUSES = [
  { v: "All", label: "All statuses" },
  { v: "current", label: "Current" },
  { v: "expiring", label: "Renewal approaching" },
  { v: "action", label: "Action required" },
  { v: "incomplete", label: "Information incomplete" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const kpi = getKPIs();
  const [country, setCountry] = useState("All");
  const [city, setCity] = useState("All");
  const [status, setStatus] = useState("All");
  const [owner, setOwner] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);

  const filtered = useMemo(
    () =>
      PROPERTIES.filter((p) => {
        if (country !== "All" && p.country !== country) return false;
        if (city !== "All" && p.city !== city) return false;
        if (status !== "All" && p.complianceStatus !== status) return false;
        if (owner !== "All" && p.ownerId !== owner) return false;
        return true;
      }),
    [country, city, status, owner]
  );

  return (
    <>
      <Topbar
        title="Portfolio overview"
        subtitle="Operational health across all properties"
        action={isAdmin ? <AddPropertyButton onClick={() => setModalOpen(true)} /> : null}
      />

      <div className="px-6 lg:px-8 py-8 space-y-8">
        {/* KPI grid */}
        <section
          data-testid="kpi-grid"
          className="grid grid-cols-2 lg:grid-cols-5 gap-4"
        >
          <KPICard
            testId="kpi-total"
            label="Total properties"
            value={String(kpi.total).padStart(2, "0")}
            hint="Across Mallorca and Ibiza"
            icon={Building2}
          />
          <KPICard
            testId="kpi-current"
            label="All current"
            value={String(kpi.current).padStart(2, "0")}
            hint="Registered requirements up to date"
            icon={CheckCircle2}
            tone="green"
          />
          <KPICard
            testId="kpi-action"
            label="Requiring action"
            value={String(kpi.requiringAction).padStart(2, "0")}
            hint="Missing or incomplete info"
            icon={AlertTriangle}
            tone="red"
          />
          <KPICard
            testId="kpi-expiring"
            label="Expiring in 30 days"
            value={String(kpi.expiringSoon).padStart(2, "0")}
            hint="Renewal approaching"
            icon={Clock}
            tone="amber"
          />
          <KPICard
            testId="kpi-expired"
            label="Critical gaps"
            value={String(kpi.expired).padStart(2, "0")}
            hint="Action required across portfolio"
            icon={CircleX}
            tone="red"
          />
        </section>

        {/* Filters + table */}
        <section className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-4 lg:px-6 py-4 border-b border-slate-200 dark:border-slate-800">
            <div>
              <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                Portfolio table
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {filtered.length} of {PROPERTIES.length} properties
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="filter-country" className="h-9 text-xs w-full lg:w-40">
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c === "All" ? "All regions" : c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={city} onValueChange={setCity}>
                <SelectTrigger data-testid="filter-city" className="h-9 text-xs w-full lg:w-44">
                  <SelectValue placeholder="City" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c === "All" ? "All cities" : c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="filter-status" className="h-9 text-xs w-full lg:w-52">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.v} value={s.v}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={owner} onValueChange={setOwner}>
                <SelectTrigger data-testid="filter-owner" className="h-9 text-xs w-full lg:w-52">
                  <SelectValue placeholder="Owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All owners</SelectItem>
                  {OWNERS.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="portfolio-table">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800/40 text-[11px] font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                  <th className="py-3 px-6 text-left">Property</th>
                  <th className="py-3 px-4 text-left">Owner</th>
                  <th className="py-3 px-4 text-left">Location</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-left">Open actions</th>
                  <th className="py-3 px-4 text-left">Next expiry</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-sm text-slate-500">
                      No properties match the current filters.
                    </td>
                  </tr>
                )}
                {filtered.map((p) => {
                  const o = getOwner(p.ownerId);
                  return (
                    <tr
                      key={p.id}
                      data-testid={`portfolio-row-${p.id}`}
                      onClick={() => navigate(`/properties/${p.id}`)}
                      className="border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-6">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{p.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                          {p.reference}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{o?.name}</td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300">
                        {p.city}
                        <span className="text-slate-400 dark:text-slate-500"> · {p.country}</span>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge tone={PROP_STATUS_TONE[p.complianceStatus]}>
                          {STATUS_LABELS[p.complianceStatus]}
                        </StatusBadge>
                      </td>
                      <td className="py-3 px-4 text-slate-900 dark:text-slate-100 tabular-nums">
                        {p.openActions}
                      </td>
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 tabular-nums">
                        {p.nextExpiryDate || "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-400">
                        <ChevronRight size={16} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <AddPropertyModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
}
